import { useEffect } from 'react';
import { useReveal } from '../components/useReveal';
import { GAMES } from '../data/games';
import { PASSES, SITE } from '../data/site';
import './HomePage.css';

const STEPS = [
  {
    n: '01',
    title: 'Open the arcade',
    body: 'No download, no app store. It loads in your browser and installs to your home screen if you want it there.',
  },
  {
    n: '02',
    title: 'Play free',
    body: 'Try a demo of any game with no account. Sign in for daily play tokens across the whole cabinet.',
  },
  {
    n: '03',
    title: 'Grab a Pass',
    body: 'Want it wide open? A Day, Week, or Month Pass unlocks unlimited plays — no tokens, no cooldowns.',
  },
];

export function HomePage() {
  useReveal('home');
  useEffect(() => {
    document.title = `${SITE.name} — The Arcade That Never Closed`;
  }, []);

  return (
    <>
      {/* ---------- HERO ---------- */}
      <section className="hero">
        <div className="hero__scene" aria-hidden="true">
          <div className="hero__stars" />
          <div className="hero__sun" />
          <div className="hero__grid" />
          <div className="hero__scanline" />
        </div>

        <div className="hero__content shell">
          <p className="hero__eyebrow eyebrow">Arcadia Amusements Inc. &middot; Est. 1984</p>
          <h1 className="hero__wordmark">
            ARCADIA<span className="hero__apos">&rsquo;</span>84
          </h1>
          <p className="hero__tagline">The arcade that never closed.</p>
          <p className="hero__sub">
            A fictional 1984 arcade, rebuilt in full 3D. {SITE.gameCount} neon cartridges —
            racers, shooters, skaters, diggers — playable free in your browser. No download. No
            quarters.
          </p>
          <div className="hero__actions">
            <a className="btn hero__play" href={SITE.appUrl}>
              PLAY FREE &#9656;
            </a>
            <a className="btn btn--ghost" href="#games">
              SEE THE GAMES
            </a>
          </div>
          <p className="hero__note">
            Free demos + free daily plays &middot; Passes from {PASSES[0].price}
          </p>
        </div>
      </section>

      <div className="divider" aria-hidden="true">
        <span />
      </div>

      {/* ---------- GAMES ---------- */}
      <section className="games" id="games">
        <div className="section-head shell reveal">
          <p className="eyebrow">The cabinet</p>
          <h2 className="section-title">Twenty cartridges, built in 3D.</h2>
          <p className="section-sub">
            Love letters to the golden age of the arcade — reimagined in full 3D and made our own. No
            licensed properties, no trademarked names, just the games we grew up feeding quarters.
          </p>
        </div>

        <div className="games__grid shell">
          {GAMES.map((g, i) => (
            <article
              key={g.id}
              className="game-card reveal"
              style={{
                ['--accent' as string]: g.accent,
                ['--accent2' as string]: g.accent2,
                transitionDelay: `${Math.min(i, 8) * 55}ms`,
              }}
            >
              <div className="game-card__band" aria-hidden="true">
                <span className="game-card__genre">{g.genre}</span>
              </div>
              <h3 className="game-card__title">{g.title}</h3>
              <p className="game-card__blurb">{g.blurb}</p>
            </article>
          ))}
        </div>
      </section>

      <div className="divider" aria-hidden="true">
        <span />
      </div>

      {/* ---------- HOW IT WORKS ---------- */}
      <section className="how">
        <div className="section-head shell reveal">
          <p className="eyebrow">How it plays</p>
          <h2 className="section-title">Insert coin. Or don&rsquo;t.</h2>
        </div>
        <div className="how__grid shell">
          {STEPS.map((s) => (
            <div className="how__step reveal" key={s.n}>
              <span className="how__num">{s.n}</span>
              <h3 className="how__title">{s.title}</h3>
              <p className="how__body">{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      <div className="divider" aria-hidden="true">
        <span />
      </div>

      {/* ---------- PASSES ---------- */}
      <section className="passes" id="passes">
        <div className="section-head shell reveal">
          <p className="eyebrow">Passes</p>
          <h2 className="section-title">Unlock the whole cabinet.</h2>
          <p className="section-sub">
            Out of daily tokens and don&rsquo;t want to wait? A Pass opens every game, unlimited, for
            its whole run. Billed securely through PayPal. Cancel anytime.
          </p>
        </div>

        <div className="passes__grid shell">
          {PASSES.map((p) => (
            <article
              key={p.id}
              className={`pass-card reveal${'featured' in p && p.featured ? ' pass-card--featured' : ''}`}
              style={{ ['--accent' as string]: p.accent }}
            >
              {'featured' in p && p.featured && <span className="pass-card__flag">MOST PLAYED</span>}
              <h3 className="pass-card__name">{p.name}</h3>
              <p className="pass-card__price">
                {p.price}
                <span className="pass-card__period">{p.period}</span>
              </p>
              <p className="pass-card__blurb">{p.blurb}</p>
              <ul className="pass-card__perks">
                <li>Unlimited plays, every cartridge</li>
                <li>No tokens, no cooldowns</li>
                <li>Scores &amp; saves across devices</li>
              </ul>
              <a className="btn pass-card__cta" href={SITE.appUrl}>
                GET {p.name}
              </a>
            </article>
          ))}
        </div>
      </section>

      {/* ---------- CLOSING CTA ---------- */}
      <section className="closer shell reveal">
        <div className="closer__card">
          <div className="closer__scene" aria-hidden="true" />
          <h2 className="closer__title">The doors are open.</h2>
          <p className="closer__sub">
            {SITE.gameCount} games, free to start, no download. Step up to the cabinet.
          </p>
          <a className="btn closer__cta" href={SITE.appUrl}>
            ENTER THE ARCADE &#9656;
          </a>
        </div>
      </section>
    </>
  );
}
